import React from 'react';
import { X, TrendingUp, ArrowRight, Sparkles, PartyPopper } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface ProgressSplashProps {
  onClose: () => void;
}

const SplashContainer = styled.div`
  position: fixed;
  padding: var(--spacing-4);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const CelebrationIcon = styled.div`
  position: relative;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

/**
 * ProgressSplash Component
 * Congratulations splash shown at halfway progress
 */
const ProgressSplash: React.FC<ProgressSplashProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const hotelId = path.split('/')[2];

  const handleGoToBoost = () => {
    onClose();
    navigate(`/upgrade?hotelId=${hotelId}`);
  };

  return (
    <SplashContainer className="animate-fadeIn">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        style={{ zIndex: -1 }}
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto overflow-hidden animate-slideUp">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
          aria-label="Close splash"
        >
          <X size={24} />
        </button>

        {/* Content Section */}
        <div className="px-8 py-10 text-center">
          {/* Celebration Icon */}
          <div className="flex items-center justify-center mb-6">
            <CelebrationIcon>
              <div className="relative">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-primary-600)' }}
                >
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
              </div>
            </CelebrationIcon>
          </div>

          {/* Congratulations Message */}
          <div className="flex items-center gap-2 w-fit mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Congratulations!
            </h2>
            <PartyPopper className="w-8 h-8 text-yellow-500 mb-3" />
          </div>

          <h3
            className="text-2xl font-semibold mb-6"
            style={{ color: 'var(--color-primary-600)' }}
          >
            You're halfway there!
          </h3>

          {/* Progress Bar */}
          <div className="w-full h-3 bg-gray-200 rounded-full mb-8 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{
                backgroundColor: 'var(--color-planet-green)',
                width: '50%',
              }}
            ></div>
          </div>

          {/* CTA Message */}
          <div
            className="mb-8 p-6 rounded-xl"
            style={{ backgroundColor: 'var(--color-primary-25)' }}
          >
            <p className="text-lg text-gray-900 font-semibold mb-2">
              Buyers are 3x more likely to select hotels with a completed
              assessment
            </p>
            <p className="text-gray-600">
              Keep going to maximize your property's visibility
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleGoToBoost}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg mb-3"
            style={{ backgroundColor: 'var(--color-primary-600)' }}
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor =
                'var(--color-primary-700)')
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor =
                'var(--color-primary-600)')
            }
          >
            Boost now for more buyer intel
            <ArrowRight size={18} />
          </button>

          {/* Continue Button */}
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 font-medium transition-colors px-4 py-2"
          >
            Continue Assessment
          </button>
        </div>
      </div>
    </SplashContainer>
  );
};

export default ProgressSplash;
